import { DocumentReference, DocumentSnapshot } from "@atgof-firebase/types";
import {
  Field,
  MultiSpecExtra,
  MultiSpecOnAdd,
  OnValueChange,
} from "../helpers/fields";
import { comparePotentiallyNumbered } from "../util";

export const takeRow: Field[] = [
  {
    key: "preferred",
    labelKey: "pref",
    kind: { flags: [{ key: "preferred", labelKey: "" }] },
  },
  { key: "timecode", kind: "shortText" },
  { key: "details", kind: "paragraph" },
  { key: "length", kind: "shortText" },
];

type OnCameraCardChange = (value: string, camera?: string | undefined) => void;

export function descriptionFields(
  cameras: string[] | undefined,
): Exclude<Field, string>[] {
  return cameras?.length
    ? cameras.map((camera) => ({
        key: "descriptions." + camera,
        labelKey: "description",
        labelSuffix: " Cam " /* TODO */ + camera,
        kind: "paragraph",
      }))
    : [{ key: "description", kind: "paragraph" }];
}

export function getRows(
  includeTakes: boolean,
  cameras: string[] | undefined,
  noSlates: boolean,
  onCameraCardChange: OnCameraCardChange | undefined = undefined,
  onSoundCardChange: OnValueChange | undefined = undefined,
  onSlateChange: OnValueChange | undefined = undefined,
  onTakeAdded: MultiSpecOnAdd | undefined = undefined,
  takesExtra: MultiSpecExtra | undefined = undefined,
): Field[][] {
  const cameraFields: Field[] = cameras
    ? cameras.map((camera) => ({
        key: "pa:cameraCardNumbers." + camera,
        labelKey: "camera",
        labelSuffix: " " + camera,
        kind: "shortText",
        onChange: onCameraCardChange
          ? (v: string) => onCameraCardChange(v, camera)
          : undefined,
      }))
    : [
        {
          key: "pa:cameraCardNumber",
          labelKey: "camera",
          kind: "paragraph", // TODO This is needed so that PA can enter multiple
          onChange: onCameraCardChange
            ? (v) => onCameraCardChange(v)
            : undefined,
        },
      ];
  return [
    [
      {
        labelKey: "card-number",
        kind: {
          composite: [
            ...cameraFields,
            {
              key: "pa:soundCardNumber",
              labelKey: "pa:sound",
              kind: "shortText",
              onChange: onSoundCardChange,
            },
          ],
        },
      },
      {
        key: "slate",
        ...(noSlates ? { labelKey: "shot" } : {}),
        kind: "shortText",
        onChange: onSlateChange,
      },
      {
        key: "sound",
        kind: { select: ["sync", "mute", "wildtrack", "guidetrack"] },
      },
    ],
    [
      ...descriptionFields(cameras),
      { key: "dialogue", kind: "paragraph" },
      { key: "notes-for-the-editor", kind: "paragraph" },
      { key: "sound-details", kind: "paragraph" },
    ],
    ...(includeTakes
      ? [
          [
            {
              key: "takes",
              multi: {
                collection: (parentRef: DocumentReference) => ({
                  ref: parentRef.collection("takes"),
                  indexField: "index",
                }),
                numbering: (v: any) =>
                  v.get("index") + 1 + (v.get("isPickup") ? "PU" : ""),
                explicitAdd: {
                  extra: {
                    icon: "table-column-plus-after",
                    data: { isPickup: true },
                  },
                },
                onAdd: onTakeAdded,
                extra: takesExtra,
              },
              kind: { composite: takeRow },
            },
          ],
        ]
      : []),
  ];
}

export function compareSheets(a: DocumentSnapshot, b: DocumentSnapshot) {
  return comparePotentiallyNumbered(a.get("pa:slate"), b.get("pa:slate"));
}
